import * as React from 'react'
import {
  CompanyRole,
  CompanyRoleError,
  CompanyRoleInput,
  emptyPermissions,
  pickPermissions,
  PermissionFlag,
  useMutationRemoveCompanyRole,
  useMutationSaveCompanyRole,
  useQueryCompanyRoles,
} from '@/lib/api/company/roles'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useConfirm } from '@/modules/shared/context/ConfirmContext'
import { toast } from '@/lib/toasts/api'
import { useDirtyStore } from '@/modules/shared/stores/dirtyStore'
import { IMPLIED_BY, roleErrorMessage, rolesKey, withImplied } from './catalog'

/** null = podgląd, number = edycja istniejącej roli, 'new' = nowa rola. */
export type EditTarget = number | 'new' | null

export type RoleEditor = ReturnType<typeof useRoleEditor>

// Wspólny stan obu układów zakładki: wybór roli, szkic, zapis, usuwanie.
export function useRoleEditor(companyId: string) {
  const { t } = useLocalize()
  const confirm = useConfirm()
  const query = useQueryCompanyRoles(companyId, { fresh: true })
  const save = useMutationSaveCompanyRole(companyId)
  const remove = useMutationRemoveCompanyRole(companyId)

  const roles = React.useMemo(() => query.data?.roles ?? [], [query.data])
  const canManage = query.data?.canManage ?? false

  const [selectedId, setSelectedId] = React.useState<number | null>(null)
  const [editing, setEditing] = React.useState<EditTarget>(null)
  const [draft, setDraft] = React.useState<CompanyRoleInput | null>(null)
  const [error, setError] = React.useState<string | null>(null)

  const selected = roles.find((r) => r.id === selectedId) ?? roles[0] ?? null
  const editedRole = typeof editing === 'number' ? roles.find((r) => r.id === editing) ?? null : null

  const isDirty = React.useMemo(() => {
    if (!draft) return false
    if (editing === 'new') return true
    if (!editedRole) return false
    const original = toInput(editedRole)
    return JSON.stringify(original) !== JSON.stringify(draft)
  }, [draft, editing, editedRole])

  // Zmiana zakładki z niezapisaną rolą pyta o potwierdzenie (SubNavigationItem).
  const setDirty = useDirtyStore((s) => s.setDirty)
  React.useEffect(() => {
    setDirty(isDirty)
    return () => setDirty(false)
  }, [isDirty, setDirty])

  const confirmDiscard = React.useCallback(async () => {
    if (!isDirty) return true
    return confirm({ title: t(rolesKey('unsaved')), message: t(rolesKey('discardConfirm')), confirmText: t(rolesKey('discard')) })
  }, [isDirty, confirm, t])

  const select = async (id: number) => {
    if (editing !== null && !(await confirmDiscard())) return
    setEditing(null)
    setDraft(null)
    setError(null)
    setSelectedId(id)
  }

  const startEdit = (role: CompanyRole) => {
    setSelectedId(role.id)
    setEditing(role.id)
    setDraft(toInput(role))
    setError(null)
  }

  const startNew = async (from?: CompanyRole) => {
    if (editing !== null && !(await confirmDiscard())) return
    setEditing('new')
    setError(null)
    setDraft(
      from
        ? {
            ...pickPermissions(from),
            name: `${from.name} ${t(rolesKey('copySuffix'))}`,
            description: from.description,
          }
        : { ...emptyPermissions(), name: '', description: '' },
    )
  }

  const cancel = async () => {
    if (!(await confirmDiscard())) return
    setEditing(null)
    setDraft(null)
    setError(null)
  }

  const toggle = (flag: PermissionFlag, value: boolean) => {
    setDraft((d) => {
      if (!d) return d
      const next = { ...d, [flag]: value }
      // Odznaczenie podglądu zdejmuje też edycję, z której podgląd wynika.
      const edit = IMPLIED_BY[flag]
      if (!value && edit) next[edit] = false
      return next
    })
  }

  // „Zaznacz wszystkie” w grupie; odznaczenie zdejmuje też edycję, z której wynika podgląd.
  const setGroup = (flags: PermissionFlag[], value: boolean) =>
    setDraft((d) => (d ? { ...d, ...Object.fromEntries(flags.map((f) => [f, value])) } : d))

  const setField = (field: 'name' | 'description', value: string) =>
    setDraft((d) => (d ? { ...d, [field]: value } : d))

  const submit = async () => {
    if (!draft) return
    setError(null)
    try {
      const payload = { ...withImplied(pickPermissions(draft)), name: draft.name, description: draft.description }
      const result = (await save.mutateAsync({
        roleId: typeof editing === 'number' ? editing : null,
        role: payload,
      })) as { id?: number } | undefined
      toast.success({ text: t(rolesKey('saved')) })
      if (result?.id) setSelectedId(result.id)
      setEditing(null)
      setDraft(null)
    } catch (e) {
      setError(e instanceof CompanyRoleError ? roleErrorMessage(t, e.code, e.message) : t(rolesKey('errors.default')))
    }
  }

  const removeRole = async (role: CompanyRole) => {
    if (!(await confirm({ title: t(rolesKey('deleteConfirm'), { name: role.name }), confirmText: t(rolesKey('delete')) })))
      return
    setError(null)
    try {
      await remove.mutateAsync(role.id)
      toast.success({ text: t(rolesKey('deleted')) })
      setEditing(null)
      setDraft(null)
      setSelectedId(null)
    } catch (e) {
      setError(e instanceof CompanyRoleError ? roleErrorMessage(t, e.code, e.message) : t(rolesKey('errors.default')))
    }
  }

  return {
    query,
    roles,
    canManage,
    selected,
    editing,
    editedRole,
    draft,
    error,
    isDirty,
    saving: save.isPending || remove.isPending,
    select,
    startEdit,
    startNew,
    cancel,
    toggle,
    setGroup,
    setField,
    submit,
    removeRole,
  }
}

const toInput = (role: CompanyRole): CompanyRoleInput => ({
  ...pickPermissions(role),
  name: role.name,
  description: role.description ?? '',
})
